import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { debounce } from 'lodash';
import { useState, useEffect, useCallback } from 'react';

import ChevronDown from '@/assets/icons/ic-chevron-down.svg';

import { IThemeProps } from '@/styles/themes/types';

const RootContainer = styled.div<IThemeProps>`
  position: relative;
  overflow: auto;
  // Padding's are set because of lecture-thumbnail's tooltips. For more info check:
  // https://stackoverflow.com/a/39554003
  padding-bottom: 150px;
  margin-bottom: -150px;

  & > button {
    opacity: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover > button {
      opacity: 1;
    }
  }
`;

const ScrollableContainer = styled.div<IThemeProps>`
  display: flex;
  overflow: auto;
  // Check previous comment
  padding-bottom: 150px;
  margin-bottom: -150px;
  ${({ theme }) => theme.legacy.hideScrollbarXStyle};
`;

const ScrollButton = styled.button<IThemeProps>`
  background: ${({ theme }) => theme.legacy.colors.white};
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.2);
  position: absolute;
  z-index: 1;
  top: calc(50% - 36px - 75px);
  width: 72px;
  height: 72px;
  border-radius: 72px;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.legacy.colors.lightGray};
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const scrollIconStyle = (direction: 'left' | 'right') => css`
  width: 32px;
  height: 32px;
  transform: rotate(${direction === 'left' ? '90' : '-90'}deg);
`;

export const ScrollableRow = ({ children }) => {
  const [isLeftArrowEnabled, setIsLeftArrowEnabled] = useState(false);
  const [isRightArrowEnabled, setIsRightArrowEnabled] = useState(false);
  const [scrollableContainerRef, setScrollableContainerRef] = useState(null);
  const scrollableContainerRefCallback = useCallback((element) => {
    setScrollableContainerRef(element);
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollableContainerRef) {
      return;
    }

    const totalScroll = scrollableContainerRef.scrollWidth - scrollableContainerRef.offsetWidth;

    setIsLeftArrowEnabled(scrollableContainerRef.scrollLeft > 0);
    setIsRightArrowEnabled(scrollableContainerRef.scrollLeft !== totalScroll);
  }, [scrollableContainerRef]);

  function handleArrowClick(direction: 'left' | 'right') {
    return () => {
      if (!scrollableContainerRef) {
        return;
      }

      const scrollOffset =
        direction === 'left' ? -scrollableContainerRef.offsetWidth / 2 : scrollableContainerRef.offsetWidth / 2;
      const newScrollPosition = scrollableContainerRef.scrollLeft + scrollOffset;

      scrollableContainerRef.scroll({ left: newScrollPosition, behavior: 'smooth' });
    };
  }

  // Debounce scroll listener to avoid event spam and cleanup debounce and event listener
  useEffect(() => {
    if (!scrollableContainerRef) {
      return () => {};
    }

    const debounceScroll = debounce(handleScroll, 50);

    // Set initial values
    handleScroll();

    window.addEventListener('resize', debounceScroll);
    scrollableContainerRef.addEventListener('scroll', debounceScroll);
    return () => {
      window.removeEventListener('resize', debounceScroll);
      scrollableContainerRef.removeEventListener('scroll', debounceScroll);
      debounceScroll.cancel();
    };
  }, [handleScroll, scrollableContainerRef]);
  return (
    <RootContainer>
      <ScrollableContainer ref={scrollableContainerRefCallback}>{children}</ScrollableContainer>
      {isLeftArrowEnabled && (
        <ScrollButton onClick={handleArrowClick('left')} css={{ left: 24 }}>
          <ChevronDown css={scrollIconStyle('left')} />
        </ScrollButton>
      )}
      {isRightArrowEnabled && (
        <ScrollButton onClick={handleArrowClick('right')} css={{ right: 24 }}>
          <ChevronDown css={scrollIconStyle('right')} />
        </ScrollButton>
      )}
    </RootContainer>
  );
};
